import React, { useState, useCallback, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { IProblemModal } from '../types/problemModal'
import { ICreateProblemLine, IProblemListItem } from '../../../../../modules/API/TeudotProblems/models/problems'
import ControlButton from '../../../../../generic/buttons/ControlButton'
import PackageIcon from '../../../../../generic/icons/IconLib/PackageBoxIcon'
import ScalesIcon from '../../../../../generic/icons/IconLib/ScaleIcon'
import ShekelIcon from '../../../../../generic/icons/IconLib/ShekelIcon'
import SearchBlockWithTitle from '../../procedures/Q8/Layouts/SearchBlockWithTitle'
import SearchInput from '../../../../../generic/inputs/inputSearch'
import { IProblemFormData } from '../types/problemModal'
import { calculateProblemCost, getProblemTypeAllowances, validateProblemsFormData } from '../utils/problemTypeUtils'
import ProductImage from '../../queries/TeudotDetails/Components/ProductImage'
import TitleRowItem from '../../kosherTeudot/generic/TitleRowItem'
import { useProblemStateManager } from '../context/ProblemStateManager'
import { handleNotifications } from '../../../../../generic/utils/handleNotifications'
import ProblemCard from './ProblemCard'

interface Props extends IProblemModal {
  onSubmit: (data: { generalRemark: string; problems: Omit<ICreateProblemLine, 'id'>[] }) => void
  isLoading?: boolean
  problemTypes: IProblemListItem[] | null
}

const ProblemModal = (props: Props) => {
  const {
    user
  } = useProblemStateManager()
  const { 
    selectedProduct,
    onSubmit,
    isLoading = false, 
    problemTypes 
  } = props
    const checkStatus = (status: number) => {
      return status !== 0
    }
  const isOfficeUser = useMemo(() => {
    return user?.rights.includes('OFFICE')
  }, [user?.rights])
  // Initialize with existing problems or create empty one
  const initializeProblems = useCallback((): IProblemFormData[] => {
    if (selectedProduct.Problems && selectedProduct.Problems.length > 0) {
      return selectedProduct.Problems.map((problem, index) => ({
        id: `existing-${index}`,
        WeightRequested: problem.WeightRequested || 0,
        QuantityRequested: problem.QuantityRequested || 0,
        PriceRequested: problem.PriceRequested || 0,
        Type: Number(problem.ProblemType) || 0,
        RemarkRequested: problem.RemarkRequested || '',
        TitleRecID: problem.TitleRecID || 0,
        RecID: problem.RecID || 0,
        ProblemType: problem.ProblemType || 0,
        Weight: problem.Weight || 0,
        Price: problem.Price || 0,
        Status: problem.Status || 0,
        LastUser: problem.LastUser || '',
        RemarkApproved: problem.RemarkApproved || '',
        QuantityApproved: problem.QuantityApproved || 0,
        WeightApproved: problem.WeightApproved || 0,
        PriceApproved: problem.PriceApproved || 0,
        Quantity: problem.Quantity || 0,
        DriverStatus: problem.DriverStatus || 0,
        DriverWorkerID: problem.DriverWorkerID || 0,
        ApprovedBy: problem.ApprovedBy || '',
        CreatedBy: problem.CreatedBy || '',
        PriceOriginal: problem.PriceOriginal || 0,
        WeightOriginal: problem.WeightOriginal || 0,
        QuantityOriginal: problem.QuantityOriginal || 0,
      }))
    } 
    
    return [{
      id: Date.now().toString(),
      WeightRequested: 0,
      QuantityRequested: 0,
      PriceRequested: 0,
      Type: 0,
      RemarkRequested: '',
      TitleRecID: 0,
      RecID: 0,
      ProblemType: 0,
      Weight: 0,
      Price: 0,
      Status: 0,
      LastUser: '',
      RemarkApproved: '',
      QuantityApproved: 0,
      WeightApproved: 0,
      PriceApproved: 0,
      Quantity: 0,
      DriverStatus: 0,
      DriverWorkerID: 0,
      ApprovedBy: '',
      CreatedBy: '',
      PriceOriginal: 0,
      WeightOriginal: 0,
      QuantityOriginal: 0,
    }]
  }, [selectedProduct.Problems])

  // Initialize general remark from existing data
  const initializeGeneralRemark = useCallback((): string => {
    return selectedProduct.RemarkRequested || ''
  }, [selectedProduct.RemarkRequested])
  
  const [generalRemark, setGeneralRemark] = useState(() => initializeGeneralRemark())
  const [problems, setProblems] = useState<IProblemFormData[]>(() => initializeProblems())
  
  const generalProductInfo = useMemo(() => {
    return {
      ProductDesc: selectedProduct.ProductDesc.trim(),
      VarietyDesc: selectedProduct.VarietyDesc.trim(),
      SizeID: selectedProduct.SizeID.trim(),
      Quantity: selectedProduct.Quantity || problems[0]?.Quantity || 0,
      Price: selectedProduct.SalePrice || problems[0]?.Price || 0,
      Weight: selectedProduct.WeightNeto || problems[0]?.Weight || 0,
    }
  }, [selectedProduct, problems])

  const handleGeneralRemarkChange = useCallback((value: string) => {
    // check if more 100 char
    if (value.length > 100) {
      handleNotifications('ההערה יכולה להכיל עד 100 תווים', {type: 'error'})
      return
    }
    setGeneralRemark(value)
  }, [])
  // Helper to calculate problem cost for display
  const getProblemCostResult = useCallback((problem: IProblemFormData) => {
    return calculateProblemCost(problem, generalProductInfo)
  }, [generalProductInfo])
  // Update state when selectedProduct changes
  useEffect(() => {
    handleGeneralRemarkChange(initializeGeneralRemark())
    setProblems(initializeProblems())
  }, [selectedProduct, initializeGeneralRemark, initializeProblems, handleGeneralRemarkChange])

  const handleAddProblem = useCallback(() => {
    const newProblem: IProblemFormData = {
      id: Date.now().toString(),
      WeightRequested: 0,
      QuantityRequested: 0,
      PriceRequested: 0,
      Type: 0,
      RemarkRequested: '',
      TitleRecID: 0,
      RecID: 0,
      ProblemType: 0,
      Weight: generalProductInfo.Weight,
      Price: generalProductInfo.Price,
      Status: 0,
      LastUser: '',
      RemarkApproved: '',
      QuantityApproved: 0,
      WeightApproved: 0,
      PriceApproved: 0,
      Quantity: generalProductInfo.Quantity,
      DriverStatus: 0,
      DriverWorkerID: 0,
      ApprovedBy: '',
      CreatedBy: user?.name || '',
      PriceOriginal: 0,
      WeightOriginal: 0,
      QuantityOriginal: 0,
    }
    setProblems(prev => [...prev, newProblem])
  }, [generalProductInfo.Weight, generalProductInfo.Price, generalProductInfo.Quantity, user?.name])

  const handleRemoveProblem = useCallback((id: string) => {
      // check if the problem was created by the same user
      const problem = problems.find(problem => problem.id === id)
      if (problem?.CreatedBy !== user?.name && problem?.CreatedBy !== '') {
        handleNotifications('אינך יכול למחוק פניה שנוצרה על ידי משתמש אחר', {type: 'warning'})
        return
      }
      setProblems(prev => prev.filter(problem => problem.id !== id))
  }, [problems, user?.name])

  const getProblemDescription = useCallback((type: number) => {
    return problemTypes?.find(problem => problem.Code === type)?.Description || ''
  }, [problemTypes])

  const handleProblemChange = useCallback((id: string, field: keyof IProblemFormData, value: string | number | boolean) => {
    // check if the problem was created by the same user
    const problem = problems.find(problem => problem.id === id)
   
    if ((problem?.CreatedBy !== user?.name && problem?.CreatedBy !== '')) {
      handleNotifications('אינך יכול לערוך פניה שנוצרה על ידי משתמש אחר', {type: 'warning'})
      return
    }
    setProblems(prev => prev.map(problem => {
      if (problem.id !== id) return problem
      
      // If changing problem type, clear all input values that are not allowed for the new type
      // This prevents invalid data from being submitted when switching between problem types
      if (field === 'RemarkRequested') {
        const remark = value as string
        if (remark.length > 100) {
          handleNotifications('ההערה יכולה להכיל עד 100 תווים', {type: 'error'})
          return {
            ...problem,
            RemarkRequested: remark.slice(0, 100)
          }
        }
        return {
          ...problem,
          RemarkRequested: remark
        }
      }
      if (field === 'isAllowToSaveEmpty') {
        return {
          ...problem,
          isAllowToSaveEmpty: value as boolean,
          WeightRequested: 0,
          QuantityRequested: 0,
          PriceRequested: 0
        }
      }
      if (field === 'Type') {
        const numericValue = Number(value)
        console.log(field)
        // If unselecting problem type (setting to 0), clear all input values
        if (numericValue <= 0) {
          return {
            ...problem,
            Type: numericValue,
            WeightRequested: 0,
            QuantityRequested: 0,
            PriceRequested: 0
          }
        }
        
        // For valid problem types, only preserve values for allowed inputs
        const allowances = getProblemTypeAllowances(numericValue, problemTypes)
        return {
          ...problem,
          Type: numericValue, // Ensure Type is always number
          // Clear values for inputs that are not allowed for the new problem type
          WeightRequested: allowances.weightAllowed ? problem.WeightRequested : 0,
          QuantityRequested: allowances.quantityAllowed ? problem.QuantityRequested : 0,
          PriceRequested: allowances.priceAllowed ? problem.PriceRequested : 0
        }
      }
      if (field === "QuantityRequested") {
        const weightPreUnit = generalProductInfo.Weight / generalProductInfo.Quantity
        const newWeight = weightPreUnit * Number(value)
       
        return {
          ...problem,
          WeightRequested: Number(newWeight.toFixed(2)),
          QuantityRequested: Number(value)
        }
      }
      return { ...problem, [field]: value }
    }))
  }, [problemTypes, problems, user?.name, generalProductInfo])

  const isFormValid = useMemo((): boolean => {
    return validateProblemsFormData(problems, problemTypes)
  }, [problems, problemTypes])

  const handleSubmit = useCallback(() => {
    if (!isFormValid) {
      return
    }
    const problemsData = problems.map(problem => ({
      WeightRequested: Number(problem.WeightRequested),
      QuantityRequested: Number(problem.QuantityRequested),
      PriceRequested: Number(problem.PriceRequested),
      Type: Number(problem.Type),
      RemarkRequested: problem.RemarkRequested.trim()
    }))
    onSubmit({
      generalRemark: generalRemark.trim(),
      problems: problemsData
    })
  }, [isFormValid, generalRemark, problems, onSubmit])

  const filteredProblemTypes = useMemo(() => {
    if (!problemTypes) return []
    return problemTypes.filter(type => !problems.some(problem => problem.Type === type.Code))
  }, [problemTypes, problems])

  return (
    <MainWrapper>
        <ProductInfo>
          <ProductImage 
            id={selectedProduct.GeneralProductID}
          />
        </ProductInfo>
        <TitlesWrapper>
          <SearchBlockWithTitle title={[
            <TitleRowItem 
              name={`
                ${generalProductInfo?.Quantity} `}
              icon={<PackageIcon />}
            />,
            <TitleRowItem 
              name={`
                ${generalProductInfo?.Weight?.toFixed(2)} `}
              icon={<ScalesIcon />}
            />,
            <TitleRowItem 
              name={`
                ${generalProductInfo?.Price?.toFixed(2)} `}
              icon={<ShekelIcon />}
            />
            ,
          ]}>
            <>
            </>
          </SearchBlockWithTitle>

        </TitlesWrapper>
      <BodyWrapper>
        {problems?.map((problem, index) => (
          <ProblemCard
            key={problem.id}
            problem={problem}
            index={index}
            isOfficeUser={isOfficeUser}
            filteredProblemTypes={filteredProblemTypes}
            problemTypes={problemTypes}
            checkStatus={checkStatus}
            getProblemCostResult={getProblemCostResult}
            handleRemoveProblem={handleRemoveProblem}
            getProblemDescription={getProblemDescription}
            handleProblemChange={handleProblemChange}
          />
        ))}
        <SearchBlockWithTitle title="הערה כללית">
          <SearchInput
            input={generalRemark}
            setInput={handleGeneralRemarkChange}
            value="תאר את הפניה הכללית..."
            iconHide
            size="full"
          />
        </SearchBlockWithTitle>
        {
          filteredProblemTypes.length > 0 && (
            <AddProblemWrapper>
              <AddProblemButton onClick={handleAddProblem}>
                + הוסף פניה נוספת
              </AddProblemButton>
            </AddProblemWrapper>
          )
        }
      </BodyWrapper>
      {isFormValid && (
        <ControlButton
          lable={isLoading ? 'שומר...' : 'שמור'}
          handleClick={handleSubmit}
          disabled={isLoading}
        />
      )}
    </MainWrapper>
  )
}

const MainWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 1em;
  overflow-y: hidden;
`

const BodyWrapper = styled.div`
  width: 90%;
  max-height: 83%;
  display: flex;
  flex-flow: column;
  gap: 1em;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 0;
  }
`

const ProductInfo = styled.div`
  width: 100%;
  padding:  0 2em 0 0;
  color: #297F76;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  gap: 0.25em;
  justify-content: center;
  align-items: center;
  /* justify-content: space-between; */
`


const AddProblemWrapper = styled.div`
  width: 100%;
  padding: 0.5em;
`

const AddProblemButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  border: 2px dashed #297F76;
  border-radius: 30px;
  background: transparent;
  color: #297F76;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgb(233,240,240, 0.2);
    border-color: #80E081;
  }
`

const TitlesWrapper = styled.div`
  width: 90%;
  display: flex;
  gap: 0.5em;
`
export default ProblemModal